import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { CoinContext } from '../../context/CoinContext';

interface HostingData {
    workerId: string;
    hostingCost: number | null;
}

interface AgreementStatus {
    status: boolean;
    message: string;
}

interface UserInfo {
    userName: string;
    email: string;
    type: string;
}

const HostingContractAgreement: React.FC = () => {
    const coinContext = useContext(CoinContext);
    const [agreementStatus, setAgreementStatus] = useState<boolean>(false);
    const [hostingData, setHostingData] = useState<HostingData[]>([]);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

    useEffect(() => {
        if (coinContext?.selectedCoin) {
            fetchAgreementStatus();
            fetchHostingData();
            fetchUserInfo();
        }
    }, [coinContext]);

    const fetchAgreementStatus = async () => {
        try {
            if (coinContext?.selectedCoin) {
                const response = await axios.get<AgreementStatus>(`/pool/worker/agreement?coin=${coinContext.selectedCoin}`);
                setAgreementStatus(response.data.status);
            }
        } catch (error) {
            console.error('Error fetching agreement status:', error);
            setError('Failed to load agreement status');
        } finally {
            setLoading(false);
        }
    };

    const fetchHostingData = async () => {
        try {
            if (coinContext?.selectedCoin) {
                const response = await axios.get<HostingData[]>(`/pool/hosting?coin=${coinContext.selectedCoin}`);
                setHostingData(response.data);
            }
        } catch (error) {
            console.error('Error fetching hosting data:', error);
            setError('Failed to load hosting data');
        }
    };

    const fetchUserInfo = async () => {
        try {
            const response = await axios.get<UserInfo>('/pool/info');
            setUserInfo(response.data);
        } catch (error) {
            console.error('Error fetching user info:', error);
            setError('Failed to load user information');
        }
    };

    const handleAgree = async () => {
        if (!isChecked) {
            setError('Please check the agreement checkbox');
            return;
        }

        try {
            setSubmitting(true);
            setError('');
            if (coinContext?.selectedCoin) {
                await axios.post(`/pool/worker/hostingcontract?coin=${coinContext.selectedCoin}`, hostingData);
                setSuccessMessage('Hosting contract agreement submitted successfully!');
                setAgreementStatus(true);
                setShowModal(false);
                setIsChecked(false);
            }
        } catch (error) {
            console.error('Error submitting agreement:', error);
            setError('Failed to submit agreement');
        } finally {
            setSubmitting(false);
        }
    };

    const formatCurrency = (amount: number | null) => {
        if (amount === null || amount === undefined) return 'N/A';
        return `$${amount.toFixed(4)}`;
    };

    const getCurrentDate = () => {
        const date = new Date();
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const generateContractReference = () => {
        const date = new Date();
        const year = date.getFullYear();
        const randomDigits = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
        const randomSuffix = Math.floor(Math.random() * 100).toString().padStart(2, '0');
        return `HC-${year}XXXX-${randomDigits}-${randomSuffix}`;
    };

    const contractText = `Hosting Contract

Contract Reference No: ${generateContractReference()}

This contract, dated ${getCurrentDate()}, is entered into between the ${userInfo?.email || 'Customer'} ("you") and
Saji FZCO, located at Address Building A1, Dubai Digital Park, DSO ("we," "us," or "Service Provider"),
for the hosting of your mining or server equipment.

Equipment and Services
Service Provider agrees to host your mining or server equipment at our facilities. The billing will be
based on actual power consumption (in kWh) and the applicable hosting rate.

Fees and Payment
● Power Consumption Charges: Hosting fees will be calculated at a rate of $0.085 per kWh.
This rate may be adjusted with a minimum of 30 days' written notice due to operational or
market changes.
● Billing Schedule: Invoices are issued on the last day of each billing cycle and are payable
by the first business day of the following month. If the due date falls on a holiday,
payment is due on the next business day.
● Installation Fees: A one-time installation fee of $600 per unit is already included in to the
purchase price.
● Repairs: Repairs outside of regular maintenance will be billed based on actual labor
and parts, with a handling/logistics markup. Estimates are available upon request.
● Taxes: The Customer is responsible for any applicable local or international taxes
associated with the ownership or use of equipment.

Service Details
● Facility Access: We will provide power, internet access, and facility space for your equipment.
Hosting is subject to space availability, and we reserve the right to delay or deny service if
no slots are available.
● Uptime Guarantee: We commit to 95% annual uptime. If actual uptime falls below this, we
will issue service credits for the excess downtime.
● Maintenance & Warranty: We will perform regular maintenance in line with equipment
guidelines. Warranty coordination will be provided when applicable, but downtime caused by 
hardware failure is not our liability.
● Network Access: A minimum of 10 Mbps network bandwidth per device will be provided.
You are responsible for security, password protection, and pool assignment requests.

Term and Termination

Term:
This agreement is valid for one year from the date your hosting service is installed and activated.
Either party may choose to end the agreement earlier by providing 30 days' written notice.
At the end of the one-year term, the agreement may be renewed upon mutual consent. Please note
that renewal terms, including the hosting rate, will be based on current market rates at the time
of renewal.

Termination for Cause:
We value transparent and cooperative relationships, but Saji FZCO reserves the right to terminate the
agreement with immediate effect if:
● Payments are not received within the agreed timeline
● A serious breach of contract remains unresolved after 5 days of written notice
● The Customer enters bankruptcy or insolvency
Our team is always happy to work together to resolve any issues before taking formal steps.

Collection Process for Non-Payment
1. Invoice Issued: Last day of each month
2. First Reminder: Within 3 business days
3. Follow-up Reminder: 4 business days later
4. Severance Notice: 5 business days after follow-up
5. Disconnection Notice: Issued within 7 business days of severance
6. Disconnection & Seizure: Actioned the next working day
7. Reconnection Fee: Reconnection fees are tiered based on the number of devices:
● Up to 10 miners: $50 flat fee
● 11–50 miners: $100 flat fee
● Over 50 miners: $150 flat fee
Reconnection is subject to slot availability and will only be initiated after full settlement of
outstanding dues.
8. If payment remains unpaid for 60 days, the Service Provider may sell or retain the equipment
to recover dues. Any surplus from a sale will be returned to the Customer. 

Customer Responsibilities
● Usage Compliance: You must comply with the Acceptable Use Policy. You may not manage
or modify the facility infrastructure.
● Legal Compliance: Your operations must comply with all applicable laws and regulations.
● Licensing and Insurance: You are responsible for securing necessary permits and insurance
for your equipment.

Communication
All communications must be directed through your assigned account manager, who will handle
billing, support, and operational updates. Accepted formats include email and approved electronic
messaging platforms.

Your contact details: ${userInfo?.email || 'Not Available'}

Intellectual Property
All content on our website—including designs, logos, scripts, and trademarks—is owned or licensed
by us. Unauthorized use, reproduction, or distribution is prohibited.

General Provisions
● No Warranty: All services are provided "as is" with no additional warranties.
● Limitation of Liability: We are not responsible for loss due to events outside our control
(e.g., power outages, internet disruptions, etc.). Liability is limited to the total paid in the
previous 3 months.
● Force Majeure: Neither party is liable for service disruption due to force majeure events.
● Assignment: You may not assign this contract without our written consent. We reserve the
right to assign it as needed.
● Governing Law: Governed by the laws of the Emirate of Abu Dhabi, with jurisdiction in
Abu Dhabi courts.
● Entire Agreement: This document represents the complete agreement. Amendments to this
agreement must be made in writing and agreed upon by both parties.`;

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-6xl mx-auto p-4 space-y-6">
            {/* Error Message */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {/* Success Message */}
            {successMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                    {successMessage}
                </div>
            )}

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                        <FontAwesomeIcon icon={faFileContract} />
                        Hosting Contract Agreement
                    </h2>
                </div>

                <div className="p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <FontAwesomeIcon
                                icon={agreementStatus ? faCheck : faTimes}
                                className={`text-lg ${agreementStatus ? 'text-green-500' : 'text-red-500'}`}
                            />
                            <span className={`font-semibold ${agreementStatus ? 'text-green-600' : 'text-red-600'}`}>
                                {agreementStatus ? 'Agreement Accepted' : 'Agreement Required'}
                            </span>
                        </div>

                        <button
                            onClick={() => setShowModal(true)}
                            className={`px-6 py-2 text-white rounded-md transition-colors ${
                                agreementStatus 
                                    ? 'bg-blue-600 hover:bg-blue-700' 
                                    : 'bg-red-600 hover:bg-red-700'
                            }`}
                        >
                            {agreementStatus ? 'View Contract' : 'Review & Agree'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Agreement Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800">
                                Hosting Contract Agreement
                            </h3>
                        </div>

                        <div className="p-6 max-h-96 overflow-y-auto">
                            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                                {contractText}
                            </pre>
                        </div>

                        {/* Agreement Actions */}
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                {!agreementStatus && (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="agreement-checkbox"
                                            checked={isChecked}
                                            onChange={(e) => setIsChecked(e.target.checked)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <label htmlFor="agreement-checkbox" className="text-sm text-gray-700">
                                            I have read and agree to the hosting contract terms
                                        </label>
                                    </div>
                                )}
                                {agreementStatus && (
                                    <div className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faCheck} className="text-green-500" />
                                        <span className="text-sm text-green-600 font-semibold">
                                            Contract Already Accepted
                                        </span>
                                    </div>
                                )}

                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            setShowModal(false);
                                            setIsChecked(false);
                                            setError('');
                                        }}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                                    >
                                        Close
                                    </button>
                                    {!agreementStatus && (
                                        <button
                                            onClick={handleAgree}
                                            disabled={!isChecked || submitting}
                                            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {submitting ? 'Submitting...' : 'Agree'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HostingContractAgreement;
